import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { PDFDocument } from 'pdf-lib';
import { ToastContext } from "../../../context/ToastContext";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import codeOfConduct from "../../../assets/Code_of_Conduct.pdf"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const useCodeOfConduct = () => {

    const gridRef = useRef();
    const tagRef = useRef();
    const { showInfo, showSuccess, showError } = useContext(ToastContext)
    const [numPages, setNumPages] = useState(null);
    const [pageWidth, setPageWidth] = useState(0);
    const containerRef = useRef(null);
    const [signatureImage, setSignatureImage] = useState(null);
    const [loader, setLoader] = useState(true);
    const [listData, setListData] = useState([]);
    const pdfFile = codeOfConduct;


    const [columnDefs] = useState([
        {
            field: "employee_id",
            headerName: "Employee ID",
            width: "30%",
            minWidth: 130,
        },
        {
            field: "user_display_name",
            headerName: "Employee Name",
            width: "30%",
            minWidth: 160,
        },
        {
            field: "code_of_conduct_file_id",
            headerName: "PDF URL",
            width: "120%",
            minWidth: 350,
            cellRenderer: (params) => {
                return <div className="text-decoration-underline text-primary c-pointer" onClick={() => { window.open(params?.value, '_blank') }}>
                    {params?.value}
                </div>
            }
        },
        {
            field: "",
            headerName: "Download",
            width: "40%",
            minWidth: 80,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => {
                return <div className="c-pointer" onClick={() => handleDownload(params?.data)}>
                    <FontAwesomeIcon icon="fa-solid fa-file-arrow-down" />
                </div>
            }
        },

    ]);

    useEffect(() => {
        setTimeout(() => {
            setLoader(false)
        }, 2000);

        if (!containerRef.current) return;
        function updateWidth() {
            setPageWidth(containerRef.current.getBoundingClientRect().width);
        }
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
    }

    const onSignatureImageChange = (e) => {
        const file = e.target.files[0];
        if (file.size > 20 * 1024 * 1024) {
            showInfo("warn", "File size must be less than 20MB.");
            return;
        }
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setSignatureImage(reader.result);
        reader.readAsDataURL(file);
    };

    const savePdfWithSignature = async () => {
        try {
            if (!signatureImage) {
                showInfo('Info', 'Please upload a signature image first.');
                return;
            }

            const existingPdfBytes = await fetch(pdfFile).then(res => res.arrayBuffer());
            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const imageBytes = await fetch(signatureImage).then(res => res.arrayBuffer());

            let image;
            if (signatureImage.startsWith('data:image/png')) {
                image = await pdfDoc.embedPng(imageBytes);
            } else if (signatureImage.startsWith('data:image/jpeg') || signatureImage.startsWith('data:image/jpg')) {
                image = await pdfDoc.embedJpg(imageBytes);
            } else {
                showInfo('Info', 'Only PNG or JPEG images are supported');
                return;
            }

            const pages = pdfDoc.getPages();
            const lastPage = pages[pages.length - 1];
            const sigWidth = 150;
            const sigHeight = (image.height / image.width) * sigWidth;
            const x = 50;
            const y = 50;
            lastPage.drawImage(image, { x, y, width: sigWidth, height: sigHeight });

            const pdfBytes = await pdfDoc.save();
            const base64String = arrayBufferToBase64(pdfBytes);

            const payload = {
                "code_of_conduct_file_id": base64String
            }
            const res = await services.getInvokeRemote("POST", Urls("UPSERTCOCDETAILS"), payload)
            if (res?.body.StatusCode === 400 || res?.body.StatusCode === 204 || res?.body.StatusCode === 202 || res?.body.StatusCode === 401 || res?.body.StatusCode === 500) {
                showError('Error', res?.body.errorMessage);
            } else if (!res?.body?.error_status) {
                showSuccess("Success", res?.body?.message);
            } else {
                showError("Error", res?.body?.message);
            }
            // const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            // const url = URL.createObjectURL(blob);
            // const a = document.createElement('a');
            // a.href = url;
            // a.download = 'Code_of_Conduct_Signed.pdf';
            // a.click();
            // URL.revokeObjectURL(url);

        } catch (error) {
            console.error(error);
            showError('Error', 'An error occurred while saving PDF with signature.');
        }
    };

    function arrayBufferToBase64(buffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }


    const getCodeOfConductList = async () => {
        try {
            const res = await services.getInvokeRemote("GET", Urls("GETCOCDETAILS"))
            if (res?.body?.StatusCode === 400 || res?.body?.StatusCode === 204 || res?.body?.StatusCode === 202 || res?.body?.StatusCode === 401 || res?.body?.StatusCode === 500) {
                showError("error", res?.body?.errorMessage);
                return;
            }
            else {
                setListData(res?.body?.data?.user_coc_data)
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleDownload = (data) => {
        try {
           fetch(data?.code_of_conduct_file_id, { method: "GET", mode: "cors" })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.blob();
                })
                .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `code_of_conduct_${data?.user_display_name}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                })
                .catch(error => {
                    showError("Error", "Failed to download PDF.");
                });
        } catch (error) {
            console.error(error);
            showError("Error", "Unexpected error occurred while downloading PDF.");
        }
    };


    return useMemo(() => ({
        numPages, pageWidth, onDocumentLoadSuccess, onSignatureImageChange, savePdfWithSignature, containerRef, signatureImage, pdfFile, columnDefs, getCodeOfConductList, gridRef, listData,tagRef
    }), [numPages, pageWidth, onDocumentLoadSuccess, onSignatureImageChange, savePdfWithSignature, containerRef, signatureImage, pdfFile, loader, columnDefs, getCodeOfConductList, gridRef, listData,tagRef])
}